import { PromiseQueue } from "@repo/lib/PromiseQueue";
import { useId, useLayoutEffect, useState } from "react";
import { objectEmpty } from "@repo/lib/objectEmpty";

export type Mutator<T> = (id: string, data: T) => void | Promise<void>;

interface ObjectCache<T extends object> {
  key: string;
  object: T | undefined;
  initialUpdate?: Partial<T>;
  refresh: { [key: string]: () => void };
}

interface DebounceCache<T extends object> {
  data: Partial<T>;
  timer: number;
  mutation: Mutator<T>;
  id: string;
}

export class MutationSystem {
  // debounce delay time
  #delay: number;
  // promise queue handler: don't run more than one mutation simultaneously
  #promiseQueue = new PromiseQueue();
  // debounce mutation cache: store cumulative mutations to an object
  #debounceCache = Object.create(null) as Record<string, DebounceCache<object>>;
  // object cache: store object data and apply mutations immediately, before save
  #cache = Object.create(null) as Record<string, ObjectCache<object>>;

  // prepare and return cache entry for key
  #prepareCache(key: string) {
    return (this.#cache[key] ??= {
      key,
      object: undefined,
      refresh: Object.create(null),
    });
  }

  // trigger all refreshers for a key
  #refresh(key: string) {
    const refreshers = this.#cache[key]?.refresh;
    if (!refreshers) return;

    for (const key in refreshers) {
      refreshers[key]();
    }
  }

  // trigger an update of properties on an object
  #update(key: string, props: object) {
    const cache = this.#prepareCache(key);

    if (cache.object) {
      Object.assign(cache.object, props);
      this.#refresh(key);
    } else {
      cache.initialUpdate ??= {};
      Object.assign(cache.initialUpdate, props);
    }
  }

  /** Immediately queue a mutation to an object */
  queue: <T>(
    key: string,
    mutation: Mutator<T>,
    object: object,
    id: string,
    update?: boolean
  ) => Promise<T>;

  constructor(delay: number = 1000) {
    this.#delay = delay;

    this.queue = this.#promiseQueue.proxy(
      async (
        key: string,
        mutation: Mutator<unknown>,
        object: object,
        id: string,
        update: boolean = true
      ) => {
        if (update) {
          this.#update(key, object);
        }
        await mutation(id, {
          ...object,
          // prevent version changes when auto-saving
          _preserveVersion: true,
        });
      }
    ) as MutationSystem["queue"];
  }

  /** Queue a mutation to an object after configured delay */
  debounce<T extends object>(
    key: string,
    mutation: Mutator<T>,
    object: Partial<T>,
    id: string,
    update: boolean = true
  ) {
    const entry = (this.#debounceCache[key] ??= {
      data: {},
      timer: -1,
      mutation: mutation as Mutator<unknown>,
      id,
    });

    Object.assign(entry.data, object);

    if (entry.timer !== -1) window.clearTimeout(entry.timer);

    entry.timer = window.setTimeout(() => {
      // if processAll already handled this mutation during the delay, bail
      if (!this.#debounceCache[key]) return;

      const { data, mutation, id } = this.#debounceCache[key];
      delete this.#debounceCache[key];
      this.queue(key, mutation, data, id, false);
    }, this.#delay);

    if (update) {
      this.#update(key, object);
    }
  }

  submitNewer<T extends object>(key: string, object: T) {
    const entryCache = this.#debounceCache[key];
    if (entryCache) Object.assign(entryCache.data, object); // update prepared mutation data, if any
    this.#update(key, object);
  }

  async processAll() {
    for (const key of Object.keys(this.#debounceCache)) {
      const { data, mutation, id } = this.#debounceCache[key];
      delete this.#debounceCache[key];
      this.queue(key, mutation, data, id, false);
    }

    await this.#promiseQueue.waitForRemaining();
  }

  /** Initiate an object. If not in cache, it will be prepared and stored. If in cache, the cache will be used instead.
   * NOTICE: This effectively disables subscriptions. This is a limitation of MutationSystem. */
  initObject<T extends object>(key: string, original: T): T;
  initObject<T extends object>(key: string, original: undefined): T | undefined;
  initObject<T extends object>(
    key: string,
    original: T | undefined
  ): T | undefined;
  initObject<T extends object>(
    key: string,
    original: T | undefined
  ): T | undefined {
    if (!original) {
      if (this.#cache[key]) {
        return this.#cache[key].object as T;
      }
      return undefined;
    }

    const cache = this.#prepareCache(key);

    if (!cache.object) {
      cache.object = { ...original };
      if (cache.initialUpdate) Object.assign(cache.object, cache.initialUpdate);
      delete cache.initialUpdate;
    }

    return cache.object as T;
  }

  /** Add a refresher for an object by key, and by subkey (subkey allows having multiple refreshers per object). */
  addRefresher(key: string, subKey: string, refresh: () => void) {
    this.#prepareCache(key).refresh[subKey] = refresh;
  }

  /** Remove a refresher for an object by the object's key, then by subkey (the refresher's key). */
  removeRefresher(key: string, subKey: string) {
    const refreshers = this.#cache[key]?.refresh;
    if (refreshers?.[subKey]) return;
    delete refreshers[subKey];
  }

  /** Is the mutation queue empty? i.e. Is everything saved? */
  ready() {
    return objectEmpty(this.#debounceCache) && !this.#promiseQueue.running();
  }
}

export function useImmediateMutationSystem<T extends object>(
  system: MutationSystem,
  key: string,
  object: T
): T;
export function useImmediateMutationSystem<T extends object>(
  system: MutationSystem,
  key: string,
  object: undefined
): T | undefined;
export function useImmediateMutationSystem<T extends object>(
  system: MutationSystem,
  key: string,
  object: T | undefined
): T | undefined;
export function useImmediateMutationSystem<T extends object>(
  system: MutationSystem,
  key: string,
  object: T | undefined
): T | undefined {
  const [, refresh] = useState({});
  const id = useId();

  useLayoutEffect(() => {
    system.addRefresher(key, id, () => refresh({}));
    return () => system.removeRefresher(key, id);
  }, [system, key, id]);

  return system.initObject(key, object);
}
